import TickerService from "../../packs/services/convertflow/elements/ticker_service";

const resolveBorderRadius = (element, variant, prefix) => {
  const option = TickerService.resolveOption(
    element.options,
    variant.options,
    `${prefix}-border-radius`,
    null,
    "cf-not-rounded"
  );

  if (option === "cf-slightly-rounded") return "5px";
  if (option === "cf-fully-rounded") return "50px";
  if (option === "custom") {
    return ["top-left", "top-right", "bottom-right", "bottom-left"]
      .map(
        (side) =>
          `${
            element.options[`${prefix}-border-radius-${side}`] ||
            variant.options[`${prefix}-border-radius-${side}`] ||
            0
          }px`
      )
      .join(" ");
  }
  return "0px";
};

export const buildTickerStylesheet = (builder, device, cta, element, variant) => {
  variant = variant || { options: {} };

  const tickerImages = TickerService.imagesFromToggleItems(
    element.content_toggle_items,
    element.id
  );

  const gap = parseInt(
    TickerService.resolveNumericOption(
      element.options,
      variant.options,
      "ticker-image-gap",
      device,
      20
    )
  );

  const maxHeight = parseInt(
    TickerService.resolveNumericOption(
      element.options,
      variant.options,
      "ticker-max-image-height",
      device,
      120
    )
  );

  const maxWidth = parseInt(
    TickerService.resolveNumericOption(
      element.options,
      variant.options,
      "ticker-max-image-width",
      device,
      300
    )
  );

  const speed = TickerService.resolveNumericOption(
    element.options,
    variant.options,
    "ticker-speed",
    null,
    50,
    1
  );

  const backgroundColor = TickerService.resolveOption(
    element.options,
    variant.options,
    "ticker-background-color",
    null,
    undefined
  );

  const borderStyle = TickerService.resolveOption(
    element.options,
    variant.options,
    "ticker-border-style",
    null,
    "none"
  );

  const borderColor = TickerService.resolveOption(
    element.options,
    variant.options,
    "ticker-border-color",
    null,
    undefined
  );

  const borderWidth = parseInt(
    TickerService.resolveNumericOption(
      element.options,
      variant.options,
      "ticker-border-width",
      null,
      1
    )
  );

  const borderRadius = resolveBorderRadius(element, variant, "ticker");

  const fadeEdges =
    TickerService.resolveOption(
      element.options,
      variant.options,
      "ticker-fade-edges",
      null,
      undefined
    ) === "true";

  const fadeGradient =
    "linear-gradient(to right, transparent, black 15%, black 85%, transparent)";

  const containerWidthStyle = TickerService.resolveOption(
    element.options,
    {},
    "ticker-container-width-style",
    device,
    "full"
  );

  const containerWidthValue = TickerService.resolveOption(
    element.options,
    {},
    "ticker-container-width-value",
    device,
    undefined
  );

  const numImages = tickerImages.length;
  const scrollDistance = numImages * (maxWidth + gap);
  const duration =
    speed > 0 && scrollDistance > 0 ? scrollDistance / speed : 0;
  const animationId = `cf-ticker-builder-${element.id}`;

  const ctaId = cta && cta.id;
  const maxWidthRule =
    ctaId && containerWidthStyle === "custom" && containerWidthValue
      ? `max-width: ${containerWidthValue}px;`
      : "";

  const scope = ctaId
    ? `#cta_${ctaId} .cf-element[data-element-id="${element.id}"] `
    : "";

  // Use a CSS class for pause and a custom property for duration so that
  // changing pause state or durations does not rewrite the `animation`
  // shorthand and restart the loop.
  // prettier-ignore
  return `${scope}.cf-ticker-element { ${maxWidthRule} }
@keyframes ${animationId} {
  from { transform: translateX(0); }
  to   { transform: translateX(-${scrollDistance}px); }
}
${scope}.cf-ticker-wrapper {
  overflow: hidden;
  width: 100%;
  ${backgroundColor ? `background: ${backgroundColor};` : ""}
  ${borderStyle && borderStyle !== "none" ? `border-style: ${borderStyle}; border-color: ${borderColor || "#000"}; border-width: ${borderWidth}px;` : ""}
  ${borderRadius !== "0px" ? `border-radius: ${borderRadius};` : ""}
  ${fadeEdges ? `mask-image: ${fadeGradient}; -webkit-mask-image: ${fadeGradient};` : ""}
}
${scope}.cf-ticker-track {
  display: flex;
  align-items: center;
}
${scope}.cf-ticker-track[data-ticker-id="${element.id}"] {
  flex-wrap: nowrap;
  gap: ${gap}px;
  will-change: transform;
  --cf-ticker-duration: ${duration > 0 ? `${duration}s` : "0s"};
  animation: ${animationId} var(--cf-ticker-duration, 0s) linear infinite;
}
${scope}.cf-ticker-track[data-ticker-id="${element.id}"].cf-ticker-paused {
  animation-play-state: paused;
}
${scope}.cf-ticker-image-slot {
  width: ${maxWidth}px;
  height: ${maxHeight}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
${scope}.cf-ticker-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  pointer-events: none;
  user-select: none;
}`;
};
